import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { UploadCloud, FileText, Trash2, Check, Loader2, AlertTriangle, Download, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageLoading } from "@/components/shared";
import { useReadOnly } from "./common";
import api from "@/lib/api";

const DOC_TYPES = ["A1_CERTIFICATE", "FREISTELLUNGSBESCHEINIGUNG_48B", "PASSPORT", "SUBUNTERNEHMER_VERTRAG", "BERUFSHAFTPFLICHTVERSICHERUNG"];
const WORKER_TYPES = ["A1_CERTIFICATE", "PASSPORT"];

const statusStyle = {
  PENDING: "border-slate-200 bg-slate-50 text-slate-500",
  AI_EXTRACTED: "border-emerald-200 bg-emerald-50 text-emerald-600",
  USER_CONFIRMED: "border-cyan/30 bg-cyan/10 text-cyan-dark",
  NEEDS_REVIEW: "border-amber-200 bg-amber-50 text-amber-600",
  ERROR: "border-red-200 bg-red-50 text-red-600",
};

export default function SubDocuments() {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const [subs, setSubs] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [docs, setDocs] = useState(null);
  const [sub, setSub] = useState("");
  const [dtype, setDtype] = useState("");
  const [worker, setWorker] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/subcon/documents").then((r) => setDocs(r.data.documents));
  useEffect(() => { api.get("/subcon/companies").then((r) => setSubs(r.data.companies)); load(); }, []);
  useEffect(() => { if (sub) api.get(`/subcon/workers?subcontractor_id=${sub}`).then((r) => setWorkers(r.data.workers)); else setWorkers([]); }, [sub]);
  useEffect(() => {
    if (!docs) return;
    if (docs.some((d) => d.ai?.status === "PENDING")) { const id = setTimeout(load, 3000); return () => clearTimeout(id); }
  }, [docs]);

  const doUpload = async (files) => {
    if (!sub || !dtype) { toast.error(t("sub.docPickFirst")); return; }
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", dtype); fd.append("subcontractor_id", sub);
      if (WORKER_TYPES.includes(dtype) && worker) fd.append("worker_id", worker);
      try {
        const { data } = await api.post("/subcon/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.duplicate_of) toast.warning(t("sub.docDup"));
        else toast.success(t("sub.docUploaded"));
      } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    }
    setUploading(false); load();
  };
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); if (readOnly) return; const files = [...(e.dataTransfer?.files || [])]; if (files.length) doUpload(files); };
  const del = async (d) => { if (!window.confirm(t("sub.docDeleteConfirm"))) return; try { await api.delete(`/subcon/documents/${d.id}`); toast.success(t("sub.deleted")); load(); } catch { toast.error("Error"); } };
  const download = async (d) => {
    try { const r = await api.get(`/subcon/documents/${d.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); window.open(url, "_blank"); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="sub-documents">
      {!readOnly && (
        <div className="mb-6 card-soft p-5" data-testid="doc-upload-panel">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">{t("sub.wkSub")} *</Label>
              <Select value={sub} onValueChange={setSub}><SelectTrigger data-testid="doc-sub-select"><SelectValue placeholder={t("sub.wkSelectSub")} /></SelectTrigger><SelectContent>{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.trading_name || s.legal_name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">{t("sub.docType")} *</Label>
              <Select value={dtype} onValueChange={setDtype}><SelectTrigger data-testid="doc-type-select"><SelectValue placeholder={t("sub.docSelectType")} /></SelectTrigger><SelectContent>{DOC_TYPES.map((dt) => <SelectItem key={dt} value={dt}>{t(`sub.dt_${dt}`)}</SelectItem>)}</SelectContent></Select>
            </div>
            {WORKER_TYPES.includes(dtype) && (
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">{t("sub.tWorkers")}</Label>
                <Select value={worker} onValueChange={setWorker}><SelectTrigger data-testid="doc-worker-select"><SelectValue placeholder={t("sub.docSelectWorker")} /></SelectTrigger><SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.first_name} {w.last_name}</SelectItem>)}</SelectContent></Select>
              </div>
            )}
          </div>
          <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
            onClick={() => fileRef.current?.click()} data-testid="doc-dropzone"
            className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed py-8 transition-colors ${dragOver ? "border-cyan bg-cyan/5" : "border-slate-200 hover:border-cyan/50"}`}>
            {uploading ? <Loader2 className="h-7 w-7 animate-spin text-cyan-dark" /> : <UploadCloud className="h-7 w-7 text-slate-400" />}
            <p className="mt-2 text-sm font-medium text-navy">{t("sub.docDrop")}</p>
            <p className="text-xs text-slate-400">{t("sub.docFormats")}</p>
            <input ref={fileRef} type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" data-testid="doc-file-input" onChange={(e) => { const f = [...(e.target.files || [])]; if (f.length) doUpload(f); e.target.value = ""; }} />
          </div>
        </div>
      )}

      {docs === null ? <PageLoading /> : docs.length === 0 ? <EmptyState icon={FileText} title={t("sub.docEmpty")} subtitle={t("sub.docEmptyHint")} /> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="sub-documents-table">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">{t("sub.docType")}</th><th className="px-4 py-3">{t("sub.docScope")}</th><th className="px-4 py-3">{t("sub.docValidUntil")}</th><th className="px-4 py-3">{t("sub.docAiStatus")}</th><th className="px-4 py-3">v</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-navy">{t(`sub.dt_${d.doc_type}`)}</div><div className="text-xs text-slate-400">{d.filename}</div></td>
                  <td className="px-4 py-3 text-slate-600">{d.worker_name || d.subcontractor_name}</td>
                  <td className="px-4 py-3 text-slate-600">{d.valid_until || "—"}</td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusStyle[d.verification_status] || statusStyle.PENDING}`}>{d.ai?.status === "PENDING" && <Loader2 className="h-3 w-3 animate-spin" />}{t(`sub.vs_${d.verification_status}`)}{d.ai?.confidence != null && d.ai?.status !== "PENDING" && ` · ${d.ai.confidence}%`}</span></td>
                  <td className="px-4 py-3 text-slate-400">{d.version}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setConfirm(d)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy" data-testid={`doc-view-${d.id}`} title={t("sub.docReview")}><ShieldCheck className="h-4 w-4" /></button>
                      <button onClick={() => download(d)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-navy" data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" /></button>
                      {!readOnly && <button onClick={() => del(d)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ReviewDialog doc={confirm} onClose={() => setConfirm(null)} onDone={load} readOnly={readOnly} t={t} />
    </div>
  );
}

function ReviewDialog({ doc, onClose, onDone, readOnly, t }) {
  const [f, setF] = useState({});
  const [vf, setVf] = useState("");
  const [vu, setVu] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (doc) {
      const ex = { ...(doc.ai?.extracted || {}) }; delete ex.confidence; delete ex.readable;
      setF(ex); setVf(doc.valid_from || ex.valid_from || ""); setVu(doc.valid_until || ex.valid_until || ex.expiry_date || "");
    }
  }, [doc]);
  if (!doc) return null;
  const save = async () => {
    setBusy(true);
    try { await api.post(`/subcon/documents/${doc.id}/confirm`, { fields: f, valid_from: vf || null, valid_until: vu || null }); toast.success(t("sub.docConfirmed")); onDone(); onClose(); }
    catch { toast.error("Error"); } finally { setBusy(false); }
  };
  const keys = Object.keys(f);
  return (
    <Dialog open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="doc-review-dialog">
        <DialogHeader><DialogTitle>{t(`sub.dt_${doc.doc_type}`)} — {t("sub.docReview")}</DialogTitle></DialogHeader>
        {doc.ai?.status === "PENDING" ? <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {t("sub.docAnalyzing")}</div> : (
          <>
            {doc.verification_status === "NEEDS_REVIEW" && <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {t("sub.docLowConf")}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">{t("sub.asgFrom")}</Label><Input type="date" value={vf ? vf.slice(0, 10) : ""} onChange={(e) => setVf(e.target.value)} data-testid="doc-valid-from" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">{t("sub.docValidUntil")}</Label><Input type="date" value={vu ? vu.slice(0, 10) : ""} onChange={(e) => setVu(e.target.value)} data-testid="doc-valid-until" /></div>
              {keys.filter((k) => !["valid_from", "valid_until", "expiry_date"].includes(k)).map((k) => (
                <div key={k} className="col-span-2 space-y-1.5"><Label className="text-xs capitalize text-slate-500">{k.replace(/_/g, " ")}</Label><Input value={f[k] == null ? "" : String(f[k])} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`doc-field-${k}`} /></div>
              ))}
            </div>
          </>
        )}
        {!readOnly && doc.ai?.status !== "PENDING" && (
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="doc-confirm-btn" className="rounded-full bg-cyan text-white hover:bg-cyan-dark">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="mr-1 h-4 w-4" /> {t("sub.docConfirm")}</>}</Button></DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
